export type ProductSeoType = 'ankle-boots' | 'boots' | 'pumps' | 'sandals'

type Localized<T> = { fr: T, en: T }
type SpecRow = { label: string, value: string }
type TextBlock = { title: string, text: string }
type FaqItem = { question: string, answer: string }
type GuideLink = { to: string, label: string }

export interface ProductSeoContent {
  specs: Localized<{ eyebrow: string, title: string, rows: SpecRow[] }>
  band: {
    eyebrow: string
    title: string
    intro: string
    blocks: TextBlock[]
    faqTitle: string
    faq: FaqItem[]
    guides: GuideLink[]
  }
}

const colours: Localized<string> = { fr: 'Noir, ivoire, carmin, safran', en: 'Black, ivory, carmine, saffron' }

export const productSeoContent: Record<ProductSeoType, ProductSeoContent> = {
  'ankle-boots': {
    specs: {
      fr: {
        eyebrow: 'Fiche technique',
        title: 'ALIGN 110, bottine à talon',
        rows: [
          { label: 'Talon', value: 'Talon aiguille de 110 mm' },
          { label: 'Tige', value: 'Cuir d’agneau italien' },
          { label: 'Semelle', value: 'Cuir, marquage Kendrick Vero Cuoio' },
          { label: 'Fermeture', value: 'Zip doré au dos' },
          { label: 'Fabrication', value: 'Assemblée à la main dans les Marches, Italie. Made in Italy' },
          { label: 'Pointures', value: 'Du 35 au 41' },
          { label: 'Coloris', value: colours.fr }
        ]
      },
      en: {
        eyebrow: 'Technical details',
        title: 'ALIGN 110 heeled ankle boot',
        rows: [
          { label: 'Heel', value: '110 mm stiletto heel' },
          { label: 'Upper', value: 'Italian lambskin' },
          { label: 'Sole', value: 'Leather, Kendrick Vero Cuoio stamp' },
          { label: 'Closure', value: 'Gold zip at the back' },
          { label: 'Made', value: 'Hand-assembled in Marche, Italy. Made in Italy' },
          { label: 'Sizes', value: 'EU 35 to 41' },
          { label: 'Colours', value: colours.en }
        ]
      }
    },
    band: {
      eyebrow: 'Bottines à talon',
      title: 'Bottine à talon de créateur, faite main en Italie.',
      intro: 'L’ALIGN 110 est une bottine à talon aiguille de 110 mm, en cuir d’agneau italien, assemblée à la main dans les Marches. Une bottine à talon de créateur pensée pour Paris : une ligne nette, un talon placé sous l’axe du corps, et une stabilité que l’on sent dès les premiers pas.',
      blocks: [
        { title: 'Bottine talon aiguille', text: 'Le talon de 110 mm est fin mais structuré : il porte le poids au centre du pied et tend la ligne de la jambe, sans vous faire basculer vers l’avant.' },
        { title: 'Bottine talon haut confortable', text: 'Cambrure travaillée, semelle vero cuoio, cuir d’agneau souple : une bottine à talon haut que l’on garde pour un dîner, un cocktail ou une longue soirée.' },
        { title: 'La petite robe noire des chaussures', text: 'Comme la petite robe noire, la bottine noire se choisit une fois, bien : elle prolonge la ligne sous une robe, tient un pantalon fluide et traverse le dîner, le cocktail et le bar d’hôtel.' },
        { title: 'Bottine de créateur', text: 'Production limitée, assemblée à la main dans les Marches, en Italie. Quatre coloris : noir, ivoire, carmin et safran.' }
      ],
      faqTitle: 'Questions sur la bottine à talon',
      faq: [
        { question: 'Quelles pointures existent pour la bottine ALIGN 110 ?', answer: 'L’ALIGN 110 est proposée du 35 au 41.' },
        { question: 'Quelle est la hauteur du talon de l’ALIGN 110 ?', answer: 'Le talon mesure 110 mm. C’est un talon aiguille pensé comme une structure : il place le poids sous l’axe du corps plutôt que d’ajouter de la hauteur pour elle-même.' },
        { question: 'Une bottine à talon aiguille est-elle confortable ?', answer: 'Elle l’est quand la cambrure, le contrefort et le cuir sont bien construits. L’ALIGN 110 est conçue autour de l’appui : le talon travaille sous l’axe, la tige en agneau épouse la cheville.' },
        { question: 'Où est fabriquée la bottine Kendrick ?', answer: 'Dans les Marches, en Italie, par assemblage manuel. La production est limitée.' },
        { question: 'Quel est le prix de la bottine à talon Kendrick ?', answer: 'L’ALIGN 110 est proposée à 680 €, en noir, ivoire, carmin et safran.' },
        { question: 'Avec quoi porter une bottine à talon noire ?', answer: 'Avec une petite robe noire, un tailleur, une robe fluide ou un jean droit : la ligne fine du talon allonge la jambe. Notre guide détaille les tenues pour dîner, cocktail et bar d’hôtel.' }
      ],
      guides: [
        { to: '/fr/magazine/bottine-talon-aiguille-guide', label: 'Bottine à talon aiguille : guide' },
        { to: '/fr/magazine/bottine-talon-haut-confortable', label: 'Bottine talon haut confortable' },
        { to: '/fr/magazine/bottine-noire-petite-robe-noire', label: 'Bottine noire et petite robe noire' },
        { to: '/fr/magazine/bottines-createur-paris', label: 'Bottines de créateur à Paris' },
        { to: '/fr/magazine/align-110-anatomie-bottine-createur', label: 'Anatomie de l’ALIGN 110' }
      ]
    }
  },
  boots: {
    specs: {
      fr: {
        eyebrow: 'Fiche technique',
        title: 'AXIS 110, botte à talon aiguille',
        rows: [
          { label: 'Talon', value: 'Talon aiguille de 110 mm' },
          { label: 'Matière', value: 'Cuir d’agneau italien' },
          { label: 'Fabrication', value: 'Assemblée à la main dans les Marches, Italie. Made in Italy' },
          { label: 'Pointures', value: 'Du 35 au 41' },
          { label: 'Coloris', value: 'Noir' }
        ]
      },
      en: {
        eyebrow: 'Technical details',
        title: 'AXIS 110 heeled boot',
        rows: [
          { label: 'Heel', value: '110 mm stiletto heel' },
          { label: 'Material', value: 'Italian lambskin' },
          { label: 'Made', value: 'Hand-assembled in Marche, Italy. Made in Italy' },
          { label: 'Sizes', value: 'EU 35 to 41' },
          { label: 'Colour', value: 'Black' }
        ]
      }
    },
    band: {
      eyebrow: 'Bottes à talon aiguille',
      title: 'Botte à talon aiguille de créateur, faite main en Italie.',
      intro: 'L’AXIS 110 est une botte noire à talon aiguille de 110 mm, en cuir d’agneau italien, assemblée à la main dans les Marches. Une botte de créateur qui allonge la ligne de la jambe et garde la même logique que toute la maison : un talon structuré, placé sous l’axe du corps.',
      blocks: [
        { title: 'Botte talon aiguille', text: 'Le talon aiguille de 110 mm donne la tenue : il porte le poids au centre du pied et prolonge la jambe d’un seul trait, du talon à la tige.' },
        { title: 'Botte de créateur noire', text: 'Cuir d’agneau italien, coloris noir : une botte qui se porte avec un manteau long, une robe midi ou un pantalon droit, de l’automne au printemps.' },
        { title: 'Bottes ou bottines ?', text: 'La botte offre une ligne plus longue et plus saisonnière ; la bottine ALIGN 110 couvre plus de mois et de tenues. Les deux partagent la même construction.' }
      ],
      faqTitle: 'Questions sur la botte à talon aiguille',
      faq: [
        { question: 'Quelles pointures existent pour la botte AXIS 110 ?', answer: 'L’AXIS 110 est proposée du 35 au 41.' },
        { question: 'Quelle est la hauteur du talon de l’AXIS 110 ?', answer: 'Le talon aiguille mesure 110 mm, comme toute la collection : un talon pensé comme une structure, qui place le poids sous l’axe du corps.' },
        { question: 'Où sont fabriquées les bottes Kendrick ?', answer: 'Dans les Marches, en Italie, par assemblage manuel. La production est limitée.' },
        { question: 'Quel est le prix de la botte AXIS 110 ?', answer: 'L’AXIS 110 est proposée à 980 €, en noir.' },
        { question: 'Botte ou bottine à talon aiguille : que choisir ?', answer: 'La bottine ALIGN 110 est plus polyvalente sur l’année ; la botte AXIS 110 donne une ligne plus longue et plus nette. Notre guide détaille les critères de choix.' }
      ],
      guides: [
        { to: '/fr/magazine/bottes-createur-guide-achat-paris', label: 'Bottes de créateur : guide d’achat' },
        { to: '/fr/magazine/chaussures-talon-luxe-bottes-cuir-createur', label: 'Talons de luxe et bottes en cuir' },
        { to: '/fr/magazine/bottines-createur-paris', label: 'Bottines de créateur à Paris' },
        { to: '/fr/magazine/talon-comme-structure', label: 'Le talon comme structure' }
      ]
    }
  },
  pumps: {
    specs: {
      fr: {
        eyebrow: 'Fiche technique',
        title: 'EDGE 110, escarpin à talon aiguille',
        rows: [
          { label: 'Talon', value: 'Talon aiguille de 110 mm' },
          { label: 'Matière', value: 'Cuir d’agneau italien' },
          { label: 'Fabrication', value: 'Assemblé à la main dans les Marches, Italie. Made in Italy' },
          { label: 'Pointures', value: 'Du 35 au 41' },
          { label: 'Coloris', value: colours.fr }
        ]
      },
      en: {
        eyebrow: 'Technical details',
        title: 'EDGE 110 heeled pump',
        rows: [
          { label: 'Heel', value: '110 mm stiletto heel' },
          { label: 'Material', value: 'Italian lambskin' },
          { label: 'Made', value: 'Hand-assembled in Marche, Italy. Made in Italy' },
          { label: 'Sizes', value: 'EU 35 to 41' },
          { label: 'Colours', value: colours.en }
        ]
      }
    },
    band: {
      eyebrow: 'Escarpins à talon aiguille',
      title: 'Escarpin à talon aiguille de créateur, fait main en Italie.',
      intro: 'L’EDGE 110 est un escarpin minimal à talon aiguille de 110 mm, en cuir d’agneau italien, assemblé à la main dans les Marches. Un escarpin de créateur où la géométrie fait le travail : le talon prolonge la ligne du corps avec une tension maîtrisée.',
      blocks: [
        { title: 'Escarpin talon aiguille', text: 'Le talon aiguille de 110 mm structure la silhouette : il place le poids au centre du pied et allonge la jambe sans durcir la démarche.' },
        { title: 'Escarpins de créateur en quatre coloris', text: 'Noir, ivoire, carmin et safran, dans le même cuir d’agneau italien et la même forme. Le noir pour l’évidence, le safran pour la couleur.' },
        { title: 'Fait main en Italie', text: 'Assemblage manuel dans les Marches, Italie. Une construction pensée pour l’appui, du talon à la pointe.' }
      ],
      faqTitle: 'Questions sur l’escarpin à talon aiguille',
      faq: [
        { question: 'Quelles pointures existent pour l’escarpin EDGE 110 ?', answer: 'L’EDGE 110 est proposé du 35 au 41.' },
        { question: 'Quelle est la hauteur du talon de l’EDGE 110 ?', answer: 'Le talon aiguille mesure 110 mm : un talon haut pensé comme une structure, qui place le poids sous l’axe du corps.' },
        { question: 'Où sont fabriqués les escarpins Kendrick ?', answer: 'Dans les Marches, en Italie, par assemblage manuel.' },
        { question: 'Quel est le prix de l’escarpin EDGE 110 ?', answer: 'L’EDGE 110 est proposé à 450 €, en noir, ivoire, carmin et safran.' },
        { question: 'Comment porter des escarpins jaunes ?', answer: 'Avec des pièces sobres : noir, denim brut ou camel. Notre guide détaille les associations.' }
      ],
      guides: [
        { to: '/fr/magazine/comment-styler-escarpins-jaunes', label: 'Comment styler des escarpins jaunes' },
        { to: '/fr/magazine/guide-talons-noirs-essentiel-garderobe', label: 'Talons noirs : l’essentiel' },
        { to: '/fr/magazine/talon-comme-structure', label: 'Le talon comme structure' },
        { to: '/fr/magazine/pourquoi-porter-talons-hauts-guide', label: 'Pourquoi porter des talons hauts' }
      ]
    }
  },
  sandals: {
    specs: {
      fr: {
        eyebrow: 'Fiche technique',
        title: 'POISE 110, sandale à talon aiguille',
        rows: [
          { label: 'Talon', value: 'Talon aiguille de 110 mm' },
          { label: 'Matière', value: 'Cuir d’agneau italien' },
          { label: 'Fabrication', value: 'Assemblée à la main en Italie. Made in Italy' },
          { label: 'Pointures', value: 'Du 35 au 41' },
          { label: 'Coloris', value: colours.fr }
        ]
      },
      en: {
        eyebrow: 'Technical details',
        title: 'POISE 110 heeled sandal',
        rows: [
          { label: 'Heel', value: '110 mm stiletto heel' },
          { label: 'Material', value: 'Italian lambskin' },
          { label: 'Made', value: 'Hand-assembled in Italy. Made in Italy' },
          { label: 'Sizes', value: 'EU 35 to 41' },
          { label: 'Colours', value: colours.en }
        ]
      }
    },
    band: {
      eyebrow: 'Sandales à talon aiguille',
      title: 'Sandale à talon aiguille de créateur, faite main en Italie.',
      intro: 'La POISE 110 est une sandale à talon aiguille de 110 mm, en cuir d’agneau italien, assemblée à la main en Italie. Une sandale de créateur à l’architecture ouverte et à la forme nette : le talon maintient la structure même dans la silhouette la plus essentielle.',
      blocks: [
        { title: 'Sandale talon aiguille', text: 'Le talon aiguille de 110 mm tient la ligne : il porte le poids au centre du pied pour une sandale construite autour de l’appui.' },
        { title: 'Sandales de créateur en quatre coloris', text: 'Noir, ivoire, carmin et safran, en cuir d’agneau italien. Le noir se porte toute l’année, la couleur fait l’été.' },
        { title: 'Fait main en Italie', text: 'Assemblage manuel en Italie : peu de pièces, bien construites.' }
      ],
      faqTitle: 'Questions sur la sandale à talon aiguille',
      faq: [
        { question: 'Quelles pointures existent pour la sandale POISE 110 ?', answer: 'La POISE 110 est proposée du 35 au 41.' },
        { question: 'Quelle est la hauteur du talon de la POISE 110 ?', answer: 'Le talon aiguille mesure 110 mm : un talon haut pensé comme une structure, qui place le poids sous l’axe du corps.' },
        { question: 'Où sont fabriquées les sandales Kendrick ?', answer: 'En Italie, par assemblage manuel.' },
        { question: 'Quel est le prix de la sandale POISE 110 ?', answer: 'La POISE 110 est proposée à 490 €, en noir, ivoire, carmin et safran.' },
        { question: 'Une sandale à talon aiguille est-elle confortable ?', answer: 'Elle l’est quand la construction soutient le pied : la POISE 110 est pensée autour de l’appui, pas seulement de la hauteur.' }
      ],
      guides: [
        { to: '/fr/magazine/talon-comme-structure', label: 'Le talon comme structure' },
        { to: '/fr/magazine/pourquoi-porter-talons-hauts-guide', label: 'Pourquoi porter des talons hauts' },
        { to: '/fr/magazine/artisanat-italien-talons-createur-francais', label: 'Artisanat italien, talons de créateur' },
        { to: '/fr/magazine/chaussures-elegantes-diner-cocktail-bar-hotel-paris', label: 'Chaussures pour dîner et cocktail' }
      ]
    }
  }
}
